import React, { useState } from 'react';
import { Box, ButtonSize, LoadingSpinner, Separator } from 'edenred-ui';
import RegistrationConfirmationImg from '@epi-assets/images/RegistrationConfirmation.png';
import { RecaptchaTerms } from '@epi-pages/Login/components';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { StyledLink } from '@epi-shared/components/StyledLink/StyledLink.styles';
import { showNotification } from '@epi-actions/overlays';
import { useLocation } from 'react-router';
import { fetchPost } from '@epi-helpers/FetchHelpers';
import { requestConfirmationLinkApiUrl } from '@epi-repositories/EmailRepository';
import { StyledButton } from './EmailToConfirm.styles';

interface EmailToConfirmProps {
  isAccountExist: boolean;
  username: string;
}

const EmailToConfirm = ({
  isAccountExist = false,
  username
}: EmailToConfirmProps) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = params.get('email');
  const usernameFromUrl = params.get('username');
  const personUsername = username ?? usernameFromUrl;
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const goToLoginPage = () => {
    dispatch(goToPage(routerPaths.login));
  };

  const requestNewLink = () => {
    setIsLoading(true);
    const onSuccess = () => {
      setIsLoading(false);
      dispatch(
        showNotification(
          { message: 'email_verification.request_new_link_success' },
          'success'
        )
      );
    };
    const onFailure = () => {
      setIsLoading(false);
      dispatch(
        showNotification(
          { message: 'email_verification.get_new_verification_link_fail' },
          'error'
        )
      );
    };

    return fetchPost(
      requestConfirmationLinkApiUrl,
      { username: personUsername },
      onSuccess,
      onFailure
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box display="flex" justifyContent="center">
      <Box position="relative" mt={6} maxWidth={900} width="100%">
        <Box
          display="flex"
          flexDirection="row"
          boxShadow="0 16px 64px 0 rgba(0, 0, 0, 0.1)"
          position="relative"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            width="50%"
            height="100%"
            overflow="hidden"
          >
            <img
              alt="registration confirmation page"
              src={RegistrationConfirmationImg}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Box>
          <Box bgcolor={theme.primaryBackgroundColor} flex={1} ml="50%">
            <Box maxWidth={450} px={5} py={4} mx="auto">
              <Box
                fontSize={theme.sizeExtraLarge}
                fontWeight={theme.fontSemiBold}
                textAlign="center"
                pt={2}
                pb={3}
              >
                {isAccountExist
                  ? t('sign_up.confirmation.header_wait_a_second')
                  : t('sign_up.confirmation.header_almost_ready')}
              </Box>
              <Box textAlign="center">
                <Box component="span">
                  {isAccountExist
                    ? t('sign_up.confirmation.confirm_email_existing')
                    : t('sign_up.confirmation.confirm_email_part_one')}
                </Box>{' '}
                {!isAccountExist && (
                  <>
                    <Box component="span" fontWeight={theme.fontSemiBold}>
                      {email}
                    </Box>
                    {t('sign_up.confirmation.confirm_email_part_two')}
                  </>
                )}
                <Box mt={-2} mb={-2}>
                  <Separator />
                </Box>
                <Box display="flex" justifyContent="center" gap="8px">
                  <Box component="span" fontWeight={theme.fontSemiBold}>
                    {t('sign_up.confirmation.did_not_get')}
                  </Box>
                  <StyledLink
                    className="link"
                    id="resend_link_btn"
                    onClick={requestNewLink}
                  >
                    {t('sign_up.confirmation.resend_link')}
                  </StyledLink>
                </Box>
                <Box mt={-2} mb={-1}>
                  <Separator />
                </Box>
                <Box display="flex" justifyContent="center">
                  <StyledButton
                    size={ButtonSize.Medium}
                    id="return-to-login-btn"
                    onClick={goToLoginPage}
                  >
                    {t('sign_up.confirmation.return_to_login')}
                  </StyledButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <RecaptchaTerms />
      </Box>
    </Box>
  );
};

export default EmailToConfirm;
